import { msg } from "@lingui/core/macro"
import type { useLingui } from "@lingui/react"

/**
 * How a voiceprint sample got into a speaker's history, rendered as a semantic
 * Mantine palette badge (never a literal hue — see .claude/rules/theming.md rule 6).
 * Shared by the tenant "Known voices" page and the super-admin speaker-enrollment
 * page so the two never drift.
 */
type TSourceKey = "voiceprint-auto" | "llm-introduction" | "user-confirmed"

const SOURCE_COLOR: Record<TSourceKey, string> = {
  "voiceprint-auto": "success",
  "llm-introduction": "info",
  "user-confirmed": "brand",
}

export interface IEnrollmentSourceDescription {
  /** Semantic Mantine palette name. */
  color: string
  label: string
  tooltip: string
}

/**
 * Describe the `confirmed_by` provenance of a voiceprint sample as a localised
 * badge label + tooltip. `confirmed_by` itself is untranslated data (a source key
 * or a user email); only the surrounding copy is localised.
 */
export function describeEnrollmentSource(
  confirmedBy: string | null,
  i18n: ReturnType<typeof useLingui>["i18n"],
): IEnrollmentSourceDescription {
  if (!confirmedBy) return { color: "gray", label: i18n._(msg`Unknown`), tooltip: i18n._(msg`No source recorded`) }
  if (confirmedBy === "voiceprint-auto") {
    return {
      color: SOURCE_COLOR[confirmedBy],
      label: i18n._(msg`Voiceprint`),
      tooltip: i18n._(msg`Auto-confirmed by voiceprint similarity match`),
    }
  }
  if (confirmedBy === "llm-introduction") {
    return {
      color: SOURCE_COLOR[confirmedBy],
      label: i18n._(msg`Introduction`),
      tooltip: i18n._(msg`AI extracted name from a self-introduction`),
    }
  }
  if (confirmedBy === "user-confirmed") {
    return {
      color: SOURCE_COLOR[confirmedBy],
      label: i18n._(msg`User confirmed`),
      tooltip: i18n._(msg`A user clicked the ✓ pill`),
    }
  }
  // Anything else is treated as "manual rename by user X".
  return { color: "warning", label: i18n._(msg`Manual rename`), tooltip: i18n._(msg`Confirmed by ${confirmedBy}`) }
}
